import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeAllEntities } from '@adornis/baseql/operations/mongo.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { ChancenDynamicClassProduct } from '@chancenwerk/_cms/products/entities/ChancenDynamicClassProduct.js';
import { ChancenDynamicClassProductFilter } from '@chancenwerk/_cms/products/entities/ChancenDynamicClassProductFilter.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { StudentType } from '@chancenwerk/db/enums/enums.js';
import { ChancenRole } from '@chancenwerk/db/semantics/ChancenRole.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { PollKV, PollLink } from '../entities/PollLink.js';

const ALLOWED_FIELDS = {
  Bundesland: 'bundesland',
  StudentID: 'studentID',
  Schulkürzel: 'kpfs.0.school.kuerzel',
  'Schüler Stadt': 'adresse.zipCity.city',
  'Schüler PLZ': 'adresse.zipCity.zip',
};

export const POLL_ALLOWED_FIELDS = ALLOWED_FIELDS;

export const CWPollEditor = stateful(
  (props: { poll?: PollLink; onDone?: (poll: Maybe<PollLink>) => Promise<void> }) => {
    const products = useObservable(
      () =>
        ChancenDynamicClassProduct.subscribeProductsByFilterPaginated(
          0,
          0,
          new ChancenDynamicClassProductFilter({}),
        )({ name: 1, class: 1, kuerzel: 1 }),
      [],
    );
    const roles = useObservable(() => subscribeAllEntities(ChancenRole)({ name: 1, readableName: 1 }), []);

    const [update, setUpdate] = useState(0);
    const form = useForm(
      props.poll ??
        new PollLink({
          mapping: [],
        }),
      {
        async onSuccess(entity) {
          if (entity._id) {
            await entity.save();
          } else {
            await entity.create();
          }
          await props.onDone?.(entity);
        },
      },
      [props.poll],
    );

    const handleAddField = (e: ValueEvent<string>) => {
      if (!e.detail.value) return;

      form.document?.mapping?.push(
        new PollKV({
          pollKey: '',
          entityField: ALLOWED_FIELDS[e.detail.value],
        }),
      );
      setUpdate(update + 1);
    };

    const handleRemoveField = (index: number) => {
      form.document?.mapping?.splice(index, 1);
      setUpdate(update + 1);
    };

    const availableFields = Object.keys(ALLOWED_FIELDS).filter(
      key => !(form.document?.mapping ?? []).find(m => m.entityField === ALLOWED_FIELDS[key]),
    );

    const studentSection = () => {
      if (form.document?.showClass !== Student._class) {
        return nothing;
      }

      return html`
        <cr-dropdown-selection
          placeholder="Unter/Oberstufe"
          .selectables=${Object.values(StudentType)}
          clearable
          ${form.field('studentType')}
        ></cr-dropdown-selection>
        <cr-dropdown-multiselection
          placeholder="Produkte"
          .selectables=${products?.map(p => p.class)}
          clearable
          ${form.field('products')}
        ></cr-dropdown-multiselection>
      `;
    };

    const employeeSection = () => {
      if (
        form.document?.showClass !== Mitarbeiter._class &&
        form.document?.showClass !== VerwaltungsMitarbeiter._class
      ) {
        return nothing;
      }
      return html`
        <cr-dropdown-multiselection
          placeholder="Rollen"
          .selectables=${roles?.map(r => r.name)}
          .renderString=${r => roles?.find(r2 => r2.name === r)?.readableName ?? r}
          clearable
          ${form.field('roles')}
        ></cr-dropdown-multiselection>
      `;
    };

    return html`
      <cr-flex space="md">
        <cr-grid columns="400px 1fr" space="lg" ${css({ alignItems: 'flex-start' })}>
          <!-- Basic Information Card -->
          <cr-flex
            space="md"
            ${css({
              minWidth: '400px',
            })}
          >
            <chancen-component-card-title ${css({ fontSize: '1.2rem', marginBottom: '16px' })}>
              Basisinformationen
            </chancen-component-card-title>

            <cr-flex space="md" vertical ${css({ gap: '16px' })}>
              <cr-input placeholder="Name der Umfrage" ${form.field('name')} ${css({ width: '100%' })}></cr-input>
              <cr-input placeholder="LamaPoll Link" ${form.field('url')} ${css({ width: '100%' })}></cr-input>
              <cr-dropdown-selection
                placeholder="Sichtbar für"
                .selectables=${[Student._class, Mitarbeiter._class, VerwaltungsMitarbeiter._class]}
                ${form.field('showClass')}
              ></cr-dropdown-selection>
              ${studentSection()} ${employeeSection()}
              <cr-checkbox label="Veröffentlicht" ${form.field('public')} ${css({ width: '100%' })}></cr-checkbox>
            </cr-flex>
          </cr-flex>

          <!-- Fields Card -->
          <cr-flex space="md">
            <chancen-component-card-title ${css({ fontSize: '1.2rem', marginBottom: '16px' })}>
              Felder
            </chancen-component-card-title>

            <cr-text ${css({ marginBottom: '8px', color: 'var(--color-text-secondary)' })}>
              Hier kannst du die Felder festlegen die aus dem CHANCENraum in die Umfrage eingefügt werden.
            </cr-text>

            <cr-dropdown-selection
              placeholder="Feld hinzufügen..."
              .selectables=${availableFields}
              value=""
              ${css({
                width: '100%',
                marginBottom: '8px',
              })}
              @value-picked=${handleAddField}
            ></cr-dropdown-selection>

            <cr-card
              ${css({
                padding: '16px',
              })}
            >
              <cr-grid
                columns="max-content 1fr max-content"
                space="md"
                ${css({
                  alignItems: 'center',
                  gap: '16px',
                  width: '100%',
                })}
              >
                <cr-text bold ${css({ color: 'var(--color-text-primary)' })}>Feld</cr-text>
                <cr-text bold ${css({ color: 'var(--color-text-primary)' })}>LamaPoll Name</cr-text>
                <cr-text bold></cr-text>

                ${(form.document?.mapping?.length ?? 0) === 0
                  ? html` <cr-text
                      ${css({
                        opacity: 0.7,
                        gridColumn: '1 / span 3',
                        textAlign: 'center',
                        padding: '16px 0',
                      })}
                    >
                      Noch kein Feld...
                    </cr-text>`
                  : nothing}
                ${repeat(form.document?.mapping ?? [], (item, i) => {
                  return html`
                    <cr-text ${css({ fontWeight: '500' })}>
                      ${Object.entries(ALLOWED_FIELDS).find(([_, v]) => v === item.entityField)?.[0]}
                    </cr-text>
                    <cr-input
                      flex
                      placeholder="LamaPoll Name"
                      ${form.field('mapping', i, 'pollKey')}
                      ${css({ width: '100%' })}
                    ></cr-input>
                    <cr-button
                      mode="icon"
                      tone="error"
                      icon="delete"
                      ${css({
                        '--button-size': '32px',
                        borderRadius: 'var(--radius-full)',
                      })}
                      @click=${() => handleRemoveField(i)}
                    ></cr-button>
                  `;
                })}
              </cr-grid>
            </cr-card>
          </cr-flex>
        </cr-grid>

        <x-flex horizontal end space="md">
          <cr-button
            ${css({
              marginTop: '8px',
              alignSelf: 'flex-end',
              fontWeight: 'bold',
            })}
            @click=${() => form.validate()}
          >
            ${props.poll ? 'Speichern' : 'Erstellen'}
          </cr-button>
          ${props.poll
            ? html`<cr-button
                tone="error"
                icon="delete"
                ${css({
                  marginTop: '8px',
                  alignSelf: 'flex-end',
                  fontWeight: 'bold',
                })}
                @click=${async () => {
                  ChromiumDialog.guard(
                    async () => {
                      await props.poll?.remove();
                      props.onDone?.(null);
                    },
                    {
                      hasToConfirm: true,
                      confirmText: 'Wirklich löschen?',
                    },
                  );
                }}
              >
                Löschen
              </cr-button>`
            : nothing}
        </x-flex>
      </cr-flex>
    `;
  },
);
